import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { UnifiedViewer, UnifiedViewerHandle } from '../components/UnifiedViewer';
import { atlasClient } from '../api/atlasClient';
import { AtlasArticle } from '../types/atlas';
import { NavContext } from '../components/Navigation';
import CircularProgress from '@mui/material/CircularProgress';

type ViewerLocationState = {
  returnTo?: string;
  returnState?: Record<string, any>;
  title?: string;
  initialStepIndex?: number;
  initialChapter?: string;
  hideLearnings?: boolean;
  label?: string;
  fromView?: string;
  hint?: string;
  dimensionFqn?: string;
  navContext?: NavContext;
  updateTimestamp?: string;
};

export const ViewerView: React.FC = () => {
  const { fqn } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as ViewerLocationState | undefined;
  const [article, setArticle] = useState<AtlasArticle | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);

  const viewerRef = useRef<UnifiedViewerHandle>(null);

  // Re-render trigger for imperative handle state
  const [, setRenderTick] = useState(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Poll viewer ref state for button display
  useEffect(() => {
    tickRef.current = setInterval(() => setRenderTick(t => t + 1), 500);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, []);

  // Fetch article on mount
  useEffect(() => {
    if (!fqn) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const decodedFqn = decodeURIComponent(fqn);
    atlasClient.getProfileByFqn(decodedFqn)
      .then((result) => {
        if (!cancelled) {
          setArticle(result);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to fetch article:', err);
          setError(err.message || 'Failed to load article');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [fqn]);

  const handleBackClick = useCallback(() => {
    if (locationState?.returnTo && locationState?.returnState) {
      // Explicit return with state (e.g., back to project page with step index)
      navigate(locationState.returnTo, { state: locationState.returnState });
    } else {
      // Pop the history stack (e.g., viewer→viewer citation drill-down)
      navigate(-1);
    }
  }, [navigate, locationState]);

  // ESC key handler
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleBackClick();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleBackClick]);

  // Navigation callback for citation drill-down
  const onNavigateToArticle = useCallback((targetFqn: string, initialChapter?: string) => {
    navigate(`/viewer/${encodeURIComponent(targetFqn)}`, {
      state: {
        initialChapter,
        returnTo: location.pathname + location.search
      }
    });
  }, [navigate, location]);

  const title = article?.subject?.name || article?.name || locationState?.title || 'Article';

  const backButton = (
    <button className="profile-header-back-button" onClick={handleBackClick} aria-label="Go back">
      <svg focusable="false" aria-hidden="true" viewBox="0 0 24 24" width="24" height="24">
        <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" />
      </svg>
    </button>
  );

  // Read imperative handle state for header buttons
  const viewerState = viewerRef.current;
  const showInfographic = viewerState?.showInfographic ?? false;
  const isDownloadingWord = viewerState?.isDownloadingWord ?? false;
  const isGeneratingInfographic = viewerState?.isGeneratingInfographic ?? false;
  const infographicCountdown = viewerState?.infographicCountdown ?? null;


  if (!fqn) {
    return (
      <div className="profile-view">
        <div className="main-content">
          <div className="viewer-header">
            <div className="viewer-header-left">
              {backButton}
              <h1 className="viewer-header-title">Article Not Found</h1>
            </div>
          </div>
          <div className="error-state">
            <p>No article ID provided.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || !article) {
    return (
      <div className="profile-view">
        <div className="main-content detail-mode">
          <div className="viewer-header">
            <div className="viewer-header-left">
              {backButton}
              <h1 className="viewer-header-title">{isLoading ? 'Loading...' : 'Article'}</h1>
            </div>
          </div>
          {isLoading ? (
            <div className="loading-state">
              <div className="loading-spinner" />
              <p>Loading article...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button onClick={() => window.location.reload()}>Retry</button>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="profile-view">
      <div className="main-content detail-mode">
        {/* New header */}
        <div className="viewer-header">
          <div className="viewer-header-left">
            {backButton}
            <h1 className="viewer-header-title">{title}</h1>
          </div>
          <div className="viewer-header-right">
                {article.versions.length > 1 && (
                  <button
                    className="viewer-header-text-btn"
                    onClick={() => setIsVersionHistoryOpen(true)}
                  >
                    Version history
                  </button>
                )}
                <button
                  className="viewer-header-filled-btn"
                  onClick={() => viewerRef.current?.downloadWord()}
                  disabled={isDownloadingWord}
                >
                  {isDownloadingWord ? 'Downloading...' : 'Download'}
                </button>
                {showInfographic && (
                  <button
                    className="viewer-header-filled-btn"
                    onClick={() => viewerRef.current?.toggleInfographic()}
                    disabled={isGeneratingInfographic}
                  >
                    {isGeneratingInfographic ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : null}
                    {isGeneratingInfographic
                      ? (infographicCountdown === 0 ? 'Any moment' : `~${infographicCountdown}s`)
                      : 'Infographic'}
                  </button>
                )}
          </div>
        </div>

        {/* Viewer body */}
        <UnifiedViewer
          ref={viewerRef}
          article={article}
          initialChapter={locationState?.initialChapter}
          hideLearnings={locationState?.hideLearnings}
          onNavigateToArticle={onNavigateToArticle}
          isVersionHistoryOpen={isVersionHistoryOpen}
          onCloseVersionHistory={() => setIsVersionHistoryOpen(false)}
        />
      </div>
    </div>
  );
};
